import { LinkStyled } from "@components/dashboard/Dashboard.styles";
import { WithOData, withOData } from "@odata/withOData";
import { isDefined } from "@utils/general";
import { logger } from "@utils/log";
import React, { memo, useContext } from "react";
import { Link } from "react-router-dom";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { PermissionContext } from "../../../contexts/permissionContext/PermissionContext";
import { IconSize } from "../../../enums";
import TestIds from "../../../testIds";
import NumberType from "../../../types/Number";
import {
    IDashboardTileComponentProps,
    IInfoTileDataCell,
    IInfoTileInfo,
    TDashboardInfoTileFormatter
} from "../../dashboard";
import DashboardTileHeader from "../../dashboard/DashboardTileHeader";
import { enhanceLinkWithDrilldownParams } from "../../drillDown/DrillDown.utils";
import { getIcon } from "../../icon";
import { getInfoValue } from "../../smart/FieldInfo";
import { useDashboardTile } from "../hooks/useDashboardTile";
import {
    ColoredIconStyled,
    Fraction,
    InfoTileBody,
    Label,
    StyledInfoTile,
    StyledInfoTileCell,
    Unit,
    Value,
    ValueWrapper
} from "./InfoTile.styles";


interface IProps extends IDashboardTileComponentProps<IInfoTileInfo>, WithOData {
    isGrouped?: boolean;
}

export const infoTileCurrencyFormatter: TDashboardInfoTileFormatter = (value) => {
    const formatted = NumberType.format(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    const parser = NumberType.getParser();
    const [number, fraction] = formatted.split(parser._decimalChar);
    return (<>
        {number}<Fraction>{parser._decimalChar}{fraction}</Fraction>
    </>);
};

interface ITileCellProps extends IInfoTileDataCell {
    formatter: TDashboardInfoTileFormatter;
    inEditMode: boolean;
    firstLetterUppercase: boolean;
}

export const InfoTileCell: React.FC<ITileCellProps> = (props) => {
    const context = useContext(AppContext);
    const { value, label, iconName, unit, severity, firstLetterUppercase, formatter } = props;

    let content: React.ReactNode;
    if (iconName) {
        const Icon = getIcon(iconName);
        content = (
                <ColoredIconStyled $severity={severity}><Icon width={IconSize.L}
                                                              height={IconSize.L}/></ColoredIconStyled>
        );
    }

    const formattedValue = formatter ? formatter(value) : value;

    content = (<>
        <ValueWrapper data-testid={TestIds.DashboardInfoTileCellValue}>
            <Value $severity={severity}>{formattedValue} {unit && <Unit>{unit}</Unit>}</Value>
            {content}
        </ValueWrapper>
        <Label data-testid={TestIds.DashboardInfoTileCellLabel}
               $firstLetterUppercase={firstLetterUppercase}>{label}</Label>
    </>);

    if (props.link) {
        const to = getInfoValue(props, "link", { context });

        if (to) {
            content = (
                    <LinkStyled $isEdit={props.inEditMode}
                                as={Link}
                                to={enhanceLinkWithDrilldownParams(to, context)}>{content}</LinkStyled>
            );
        }
    }

    return (
            <StyledInfoTileCell $span={props.size ?? 1} data-testid={TestIds.DashboardInfoTileCell}>
                {content}
            </StyledInfoTileCell>
    );
};

const InfoTile: React.FC<IProps> = ({ inEditMode, info, oData, isGrouped }) => {
    const context = useContext(AppContext);
    const permissionContext = useContext(PermissionContext);
    const { data, isLoading, hasError } = useDashboardTile<IInfoTileDataCell[]>({
        getTileDataArgs: {
            prop: info.infoData,
            args: { context, permissionContext, oData }
        }
    });

    let isAnyItemsSizeDefined = false;
    const max = data?.reduce((max, item) => {
        isAnyItemsSizeDefined = isAnyItemsSizeDefined || isDefined(item.size);
        return max + (item.size ?? 1);
    }, 0);
    if (isAnyItemsSizeDefined && data.length > 1 && max !== info.size?.w) {
        logger.warn(`Possible dashboard tile misconfiguration (${info.title}) - width of single dashboard tiles should match the width of whole widget`);
    }

    const content = (<>
        <DashboardTileHeader tooltip={info.tooltip}
                             small={isGrouped}>
            {info.title}
        </DashboardTileHeader>
        <InfoTileBody $columns={max}>
            {data?.map((item, idx) => (
                    <InfoTileCell key={idx} {...item} inEditMode={inEditMode}
                                  firstLetterUppercase={info.firstLetterUppercase}
                                  formatter={item.formatter ?? info.formatter}/>))}
        </InfoTileBody>
    </>);

    const showLoader = isLoading || hasError/*todo: may be different look for error state*/;
    const to = getInfoValue(info, "link", { context });

    return (
            <StyledInfoTile backgroundColor={info.backgroundColor}
                            inEditMode={inEditMode}
                            isLoading={showLoader}
                            hideBackground={isGrouped}
                            to={to}>
                {content}
            </StyledInfoTile>
    );
};

export default memo(withOData(InfoTile));